import { useEffect, useState, useCallback, useMemo } from 'react';
import { KeyValue } from '@mews/enums';

type ContainerElements = HTMLElement | Array<HTMLElement | undefined | null> | undefined | null;
export const FOCUSABLE_ELEMENTS_SELECTOR =
    'button:not([tabindex="-1"]), [href], input:not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

const useFocusTrap = (containerElements: ContainerElements = null) => {
    const containers = useMemo(
        (): HTMLElement[] =>
            containerElements ? [containerElements].flat().filter((el): el is HTMLElement => !!el) : [],
        [containerElements]
    );

    const handleTabKey = useCallback(
        (event: KeyboardEvent) => {
            if (event.key !== KeyValue.Tab) return;

            event.preventDefault();

            const focusableElements = containers.flatMap((el) => {
                const childElements = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR));

                if (el.matches(FOCUSABLE_ELEMENTS_SELECTOR)) {
                    return [el, ...childElements];
                }

                return childElements;
            });

            if (!focusableElements.length) {
                return;
            }

            const { target, shiftKey } = event;
            const currentIndex = focusableElements.findIndex((el) => el === target);

            let nextIndex: number;

            if (shiftKey) {
                if (currentIndex === -1 || currentIndex === 0) {
                    nextIndex = focusableElements.length - 1;
                } else {
                    nextIndex = currentIndex - 1;
                }
            } else if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
                nextIndex = 0;
            } else {
                nextIndex = currentIndex + 1;
            }

            focusableElements[nextIndex]?.focus();
        },
        [containers]
    );

    useEffect(() => {
        if (containers.length === 0) return;

        containers.forEach((el) => el.addEventListener('keydown', handleTabKey));
        return () => containers.forEach((el) => el.removeEventListener('keydown', handleTabKey));
    }, [containers, handleTabKey]);
};

export interface WithFocusTrapProps {
    registerFocusTrap: (el: ContainerElements) => void;
    deregisterFocusTrap: () => void;
}

export const withFocusTrap = <P extends WithFocusTrapProps>(Component: React.ComponentType<P>) => {
    type HOCProps = Omit<P, 'registerFocusTrap' | 'deregisterFocusTrap'>;

    const WithFocusTrap: React.FC<HOCProps> = (props) => {
        const [containerElements, setContainerElements] = useState<ContainerElements>();

        const getContainerElements = (el: ContainerElements) => {
            setContainerElements(el);
        };

        const resetFocusTrap = () => {
            setContainerElements([]);
        };

        useFocusTrap(containerElements);

        return (
            <Component
                {...(props as P)}
                registerFocusTrap={getContainerElements}
                deregisterFocusTrap={resetFocusTrap}
            />
        );
    };

    return WithFocusTrap;
};

export default useFocusTrap;
